import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import SectionHeader from "@/components/ui/SectionHeader";
import { siteImages } from "@/lib/siteData";
import { Mail } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

type TeamRow = Database["public"]["Tables"]["team_members"]["Row"];

const AboutPage = () => {
  const [team, setTeam] = useState<TeamRow[]>([]);
  const [teamLoaded, setTeamLoaded] = useState(false);
  useEffect(() => {
    supabase.from("team_members").select("*").eq("active", true).order("display_order")
      .then(({ data }) => { setTeam(data ?? []); setTeamLoaded(true); });
  }, []);
  return (
  <div className="pt-20">
    <Helmet>
      <title>About MyStudio | Columbus GA Photo & Video Studio</title>
      <meta name="description" content="Meet the team behind MyStudio — Columbus, GA's premier photography and videography rental studio since 2017." />
      <link rel="canonical" href="https://bookmystudiospace.com/about" />
      <meta property="og:title" content="About MyStudio | Columbus GA Photo & Video Studio" />
      <meta property="og:description" content="Meet the team behind MyStudio — Columbus, GA's premier photography and videography rental studio since 2017." />
      <meta property="og:url" content="https://bookmystudiospace.com/about" />
      <meta property="og:image" content={`https://bookmystudiospace.com${siteImages.consultationImage}`} />
    </Helmet>
    {/* Hero */}
    <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${siteImages.heroSlider1})` }} />
      <div className="absolute inset-0 bg-background/70" />
      <div className="relative z-10 text-center">
        <h1 className="text-4xl md:text-6xl font-display font-extrabold tracking-widest mb-4">ABOUT MYSTUDIO — YOUR CREATIVE SPACE IN COLUMBUS, GA</h1>
        <p className="text-primary font-body text-lg">Know Us Better</p>
      </div>
    </section>

    {/* Beauty Station Info */}
    <section className="py-0">
      <div className="grid grid-cols-1 lg:grid-cols-2">
        <div className="aspect-square lg:aspect-auto overflow-hidden">
          <img
            src="/__l5e/assets-v1/2bba9a3e-18c8-447a-acd2-7f485e8d2ea3/BeautificationStation_BrianaSharae-scaled.jpg"
            alt="Beauty Station"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="p-8 lg:p-16 flex flex-col justify-center text-center">
          <h2 className="text-lg md:text-xl font-display font-extrabold uppercase tracking-widest mb-6">
            THE BEAUTIFICATION STATION IS A 6X6 FOOT AREA PRIMARILY USED FOR BOTH HAIR AND MAKEUP APPLICATIONS AND CURRENTLY BOOKS AT $25/HOUR.
          </h2>
          <div className="inline-block border border-border px-6 py-2 mx-auto mb-6">
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">
              OTHER ADDONS ARE AVAILABLE AT AN ADDITIONAL CHARGE.
            </p>
          </div>
          <p className="text-foreground/80 font-body leading-relaxed mb-8 max-w-xl mx-auto">
            There is one larger mirror centered with wall shelving for Stylists and MUAs to place their utensils and supplies. We have a stool with the option to adjust the height as needed. We have an 18 inch Ring Light that is included as well. The area can be curtained off if you don't want interference from other users of the space.
          </p>
          <Link to="/book?studio=beauty-station">
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-display font-bold uppercase tracking-widest px-8">
              Book Beauty Station
            </Button>
          </Link>
        </div>
      </div>
    </section>

    {/* Our Story */}
    <section className="py-20 bg-surface">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-2xl font-display font-extrabold tracking-widest mb-4">OUR STORY</h2>
            <p className="text-foreground/80 font-body leading-relaxed mb-4">
              Founded in 2017 with the sole intent to assist creatives with a place where they could create strong studio content.
              MyStudio has grown from a single room with basic equipment into Columbus, GA's premier creative space.
            </p>
            <p className="text-foreground/80 font-body leading-relaxed mb-4">
              We believe every creative — from first-time portrait photographers to seasoned filmmakers — deserves access to professional-grade
              facilities without the professional-grade price tag. That belief drives everything we do.
            </p>
            <p className="text-foreground/80 font-body leading-relaxed">
              Today, MyStudio operates two purpose-built studios, offers a full suite of in-house production services,
              and serves as a hub for the Columbus creative community.
            </p>
          </div>
          <div className="aspect-video bg-panel overflow-hidden border border-border">
            <img src={siteImages.consultationImage} alt="MyStudio space" className="w-full h-full object-cover" />
          </div>
        </div>
      </div>
    </section>

    {/* Facilities */}
    <section className="py-20">
      <div className="container mx-auto px-4">
        <SectionHeader title="OUR FACILITIES" subtitle="We have additional options available for you" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { title: "In-House Professionals", desc: "Photographers, videographers, and editors available for hire.", img: siteImages.studioAid },
            { title: "Props Available", desc: "Furniture, backdrops, and styling props for any shoot.", img: siteImages.studioProp },
            { title: "Set Builds & Designs", desc: "Custom set construction for commercial and creative projects.", img: siteImages.studioSet },
            { title: "Pro Equipment", desc: "Sony, Canon, Lumix, Blackmagic Design, Savage gear on hand." },
          ].map((f) => (
            <div key={f.title} className="bg-panel border border-border overflow-hidden">
              {f.img && (
                <div className="aspect-video overflow-hidden">
                  <img src={f.img} alt={f.title} className="w-full h-full object-cover" loading="lazy" />
                </div>
              )}
              <div className="p-6">
                <h3 className="font-display font-bold tracking-wider text-lg mb-2">{f.title}</h3>
                <p className="text-sm text-muted-foreground font-body">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
        {/* Equipment Logos */}
        <div className="flex items-center justify-center gap-8 md:gap-12 flex-wrap mt-12 opacity-50">
          {[siteImages.sonyLogo, siteImages.canonLogo, siteImages.lumixLogo, siteImages.blackmagicLogo, siteImages.savageLogo].map((l, i) => (
            <img key={i} src={l} alt="Equipment brand" width={120} height={24} className="h-6 w-auto object-contain invert" loading="lazy" />
          ))}
        </div>
      </div>
    </section>

    {/* Team — live data; hides if empty */}
    {(!teamLoaded || team.length > 0) && (
      <section className="py-20 bg-surface">
        <div className="container mx-auto px-4">
          <SectionHeader title="THE TEAM" subtitle="We all shoot here in some capacity" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {team.map((m) => {
              const socials = (m.socials ?? {}) as Record<string, string>;
              return (
                <div key={m.id} className="bg-panel border border-border overflow-hidden text-center group hover:border-primary/50 transition-colors">
                  <div className="aspect-[3/4] overflow-hidden">
                    {m.photo_url && <img src={m.photo_url} alt={m.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />}
                  </div>
                  <div className="p-6">
                    <h3 className="font-display font-bold tracking-wider text-lg">{m.name}</h3>
                    {m.role && <p className="text-sm text-muted-foreground font-body mb-3">{m.role}</p>}
                    {m.bio && <p className="text-xs text-muted-foreground font-body mb-3">{m.bio}</p>}
                    <div className="flex justify-center gap-3">
                      {socials.email && (
                        <a href={`mailto:${socials.email}`} className="text-muted-foreground hover:text-primary transition-colors" aria-label={`Email ${m.name}`}>
                          <Mail className="h-4 w-4" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    )}

    {/* CTA */}
    <section className="py-20 text-center">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl md:text-5xl font-display font-extrabold tracking-widest mb-6">COME CREATE WITH US</h2>
        <Link to="/book">
          <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-display font-bold uppercase tracking-widest px-10 text-lg">
            Book Now
          </Button>
        </Link>
      </div>
    </section>
  </div>
  );
};

export default AboutPage;
